/**
 * ReviewQueue — S2 Task 9 (spec 2026-07-16 §3.1/§4, veredito visual TL).
 *
 * A fila de revisão é o CORAÇÃO da UX de Respostas Prontas: nenhum item vai
 * para o cliente sem passar por aqui. Renderiza acima do `.composer-r7b__box`
 * (bloco irmão — NUNCA sequestra o composer/textarea, que seguem operáveis).
 *
 * Montagem (1x, via `useState(() => …)`): `response.items` → `QueueItemState[]`
 * com variáveis camada 1/2 já aplicadas (`applyCannedVariables` — `@lume/shared`).
 * `burstKey` gerado 1x na montagem (mesmo padrão `optimisticId` de
 * `use-send-message.ts:178`). O HOST (`Composer`) deve renderizar com
 * `key={response.id}` — trocar de resposta pronta é um REMOUNT completo
 * (reseta burstKey/itens/estado — nunca mistura duas rajadas).
 *
 * Bloqueio (âmbar, tokens `--warning-*`): qualquer item `pending` com
 * `unresolved.length > 0` OU mídia num canal ≠ telegram. A nota por-item
 * distingue camada 2 (nome está em `CANNED_VARIABLE_CATALOG` com `layer===2`)
 * de camada 1, e dentro de camada 2 usa a prop `verified` (fix pós-review
 * Important #2): não-verificado → "exige titular verificado"; verificado
 * (var null só pode ser falha de ERP/MCP — `variable-resolver.service.ts`)
 * → "não foi possível obter o dado do contrato".
 *
 * Envio: `useSendBurst` — payload SEMPRE reconstrói a partir do array
 * completo passado (nunca filtra por status), preservando índice==posição.
 * Falha parcial (200 com itens failed/skipped) mantém a fila SÓ com esses
 * itens (read-only) + banner + "Tentar novamente" reenviando o MESMO
 * `burstKey` e o snapshot COMPLETO original (o servidor deduplica os já-`sent`
 * via idempotência `burstKey:index` — nunca reenviar uma lista filtrada, ou
 * os índices dessincronizam). Erro HTTP inteiro (throw) não mexe na fila.
 */
import { CANNED_VARIABLE_PATTERN } from '@lume/shared';
import {
  aggregateCannedBlockReason,
  applyCannedBurstResult,
  buildCannedQueueItems,
  cannedItemNote,
  cannedQueueItemToBurstRequest,
  editCannedQueueItem,
  evaluateCannedMediaBlock,
} from '@lume/shared/canned-responses';
import { ArrowDown, ArrowUp, GripVertical, Pencil, X } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useMediaAssets, useSendBurst } from '../../../../hooks/use-canned-responses';
import { ApiError } from '../../../../lib/api-client';
import { useMediaAssetObjectUrl } from '../../../canned-responses/use-media-asset-object-url';

import type {
  BurstResultWire,
  CannedResponseWire,
  MediaAssetWire,
} from '../../../../lib/api-client';
import type { ChannelKind } from '@lume/shared';
import type { CannedQueueItem } from '@lume/shared/canned-responses';
import type { JSX, ReactNode } from 'react';

/** @deprecated alias local — mantido p/ imports existentes; = `CannedQueueItem` de `@lume/shared/canned-responses`. */
export type QueueItemState = CannedQueueItem;

export interface ReviewQueueProps {
  conversationId: string;
  channel: ChannelKind;
  response: CannedResponseWire;
  variables: Record<string, string | null>;
  /**
   * Fix pós-review T9 (Important #2) — `conversation.identity?.verified === true`
   * do host. Distingue a nota âmbar de var camada 2 não-resolvida: NÃO
   * verificado → "exige titular verificado"; verificado (a var só pode ter
   * falhado por ERP/MCP) → "não foi possível obter o dado do contrato" — a
   * copy antiga MENTIRIA nesse ramo.
   */
  verified: boolean;
  onDismiss: () => void;
}

/** Renderiza o texto ORIGINAL (com `{{nome}}`) marcando trechos resolvidos/pendentes. */
function renderTemplateText(
  rawTemplate: string,
  variables: Record<string, string | null | undefined>,
): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  for (const m of rawTemplate.matchAll(CANNED_VARIABLE_PATTERN)) {
    const idx = m.index ?? 0;
    if (idx > lastIndex) {
      nodes.push(<span key={key++}>{rawTemplate.slice(lastIndex, idx)}</span>);
    }
    const name = m[1] ?? '';
    const value = variables[name];
    if (typeof value === 'string' && value.length > 0) {
      nodes.push(
        <mark
          key={key++}
          style={{
            background: 'var(--brand-subtle)',
            color: 'var(--brand-subtle-fg, inherit)',
            borderRadius: 3,
            padding: '0 2px',
          }}
        >
          {value}
        </mark>,
      );
    } else {
      nodes.push(
        <span
          key={key++}
          className="font-mono"
          style={{
            border: '1px dashed var(--warning-border)',
            borderRadius: 3,
            padding: '0 3px',
            color: 'var(--warning-fg)',
          }}
        >
          {`{{${name}}}`}
        </span>,
      );
    }
    lastIndex = idx + m[0].length;
  }
  if (lastIndex < rawTemplate.length) {
    nodes.push(<span key={key++}>{rawTemplate.slice(lastIndex)}</span>);
  }
  return nodes;
}

export function ReviewQueue({
  conversationId,
  channel,
  response,
  variables,
  verified,
  onDismiss,
}: ReviewQueueProps): JSX.Element {
  const burstKeyRef = useRef<string | null>(null);
  burstKeyRef.current ??= `burst_${Date.now().toString()}_${Math.random().toString(36).slice(2, 10)}`;
  const burstKey = burstKeyRef.current;

  const [items, setItems] = useState<QueueItemState[]>(() =>
    buildCannedQueueItems(response.items, variables),
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editedIds, setEditedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [partialFailure, setPartialFailure] = useState<{
    atItem: number;
    remaining: number;
  } | null>(null);
  const [httpErrorMessage, setHttpErrorMessage] = useState<string | null>(null);
  const lastSnapshotRef = useRef<QueueItemState[] | null>(null);

  const mediaQ = useMediaAssets(true);
  const mediaById = useMemo(
    () => new Map<string, MediaAssetWire>((mediaQ.data?.data ?? []).map((m) => [m.id, m])),
    [mediaQ.data],
  );

  const rawTemplateById = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of response.items) {
      map.set(item.id, item.kind === 'text' ? item.text : (item.caption ?? ''));
    }
    return map;
  }, [response.items]);

  const sendBurst = useSendBurst(conversationId);

  const startEdit = useCallback((item: QueueItemState) => {
    setEditingId(item.itemId);
    setEditValue(item.kind === 'text' ? item.text : (item.caption ?? ''));
  }, []);

  const commitEdit = useCallback((itemId: string, value: string) => {
    setItems((prev) =>
      prev.map((it) => (it.itemId === itemId ? editCannedQueueItem(it, value) : it)),
    );
    setEditedIds((prev) => {
      const next = new Set(prev);
      next.add(itemId);
      return next;
    });
    setEditingId(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
  }, []);

  const removeItem = useCallback((itemId: string) => {
    setItems((prev) => prev.filter((it) => it.itemId !== itemId));
  }, []);

  const moveItem = useCallback((itemId: string, direction: -1 | 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.itemId === itemId);
      if (idx === -1) return prev;
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const a = prev[idx];
      const b = prev[target];
      if (a === undefined || b === undefined) return prev;
      const next = [...prev];
      next[idx] = b;
      next[target] = a;
      return next;
    });
  }, []);

  const blockedItems = items.filter(
    (it) => it.unresolved.length > 0 || evaluateCannedMediaBlock(it, channel, mediaById) !== null,
  );
  const isBlocked = blockedItems.length > 0;
  const pendingCount = items.filter((it) => it.status === 'pending').length;

  const runSend = useCallback(
    async (snapshot: QueueItemState[]): Promise<void> => {
      setHttpErrorMessage(null);
      const payload = snapshot.map(cannedQueueItemToBurstRequest);
      try {
        const result: BurstResultWire = await sendBurst.mutateAsync({
          cannedResponseId: response.id,
          burstKey,
          items: payload,
        });
        const byIndex = new Map(result.items.map((r) => [r.index, r] as const));
        const updated = snapshot.map((it, i) => {
          const r = byIndex.get(i);
          return r === undefined ? it : applyCannedBurstResult(it, r);
        });
        lastSnapshotRef.current = updated;
        const allSent = updated.every((it) => it.status === 'sent');
        if (allSent) {
          onDismiss();
          return;
        }
        const remainingVisible = updated.filter((it) => it.status !== 'sent');
        setItems(remainingVisible);
        const firstNotSentIdx = updated.findIndex((it) => it.status !== 'sent');
        setPartialFailure({
          atItem: firstNotSentIdx === -1 ? updated.length : firstNotSentIdx + 1,
          remaining: remainingVisible.length,
        });
      } catch (err) {
        setHttpErrorMessage(
          err instanceof ApiError ? err.message : 'Falha ao enviar a sequência. Tente novamente.',
        );
      }
    },
    [burstKey, onDismiss, response.id, sendBurst],
  );

  const handleSendClick = useCallback(() => {
    lastSnapshotRef.current = items;
    void runSend(items);
  }, [items, runSend]);

  const handleRetryClick = useCallback(() => {
    const snapshot = lastSnapshotRef.current;
    if (snapshot === null) return;
    void runSend(snapshot);
  }, [runSend]);

  const isReadOnly = partialFailure !== null;
  const isSending = sendBurst.isPending;

  return (
    <div
      className="mb-2 flex flex-col overflow-hidden rounded-lg border"
      style={{ borderColor: 'var(--border-strong)', background: 'var(--bg-surface)' }}
      data-testid="review-queue"
    >
      <div
        className="flex items-center gap-2 border-b px-3 py-2"
        style={{ borderColor: 'var(--border-subtle)' }}
      >
        <span aria-hidden style={{ color: 'var(--brand)' }}>
          ⚡
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium" style={{ color: 'var(--fg-1)' }}>
            {response.title}
          </div>
          <div
            className="text-2xs"
            style={{ color: 'var(--fg-3)' }}
            data-testid="review-queue-count"
          >
            {items.length} {items.length === 1 ? 'item' : 'itens'} · revisar antes de enviar
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Descartar fila de revisão"
          data-testid="review-queue-dismiss"
          className="composer-r7b__icon-btn"
        >
          <X size={14} aria-hidden />
        </button>
      </div>

      <div>
        {items.map((item, idx) => (
          <QueueItemCard
            key={item.itemId}
            index={idx}
            item={item}
            channel={channel}
            variables={variables}
            verified={verified}
            mediaById={mediaById}
            rawTemplate={rawTemplateById.get(item.itemId)}
            isEdited={editedIds.has(item.itemId)}
            isEditing={editingId === item.itemId}
            editValue={editValue}
            onEditValueChange={setEditValue}
            onStartEdit={() => startEdit(item)}
            onCommitEdit={() => commitEdit(item.itemId, editValue)}
            onCancelEdit={cancelEdit}
            onRemove={() => removeItem(item.itemId)}
            onMoveUp={() => moveItem(item.itemId, -1)}
            onMoveDown={() => moveItem(item.itemId, 1)}
            canMoveUp={idx > 0}
            canMoveDown={idx < items.length - 1}
            readOnly={isReadOnly}
          />
        ))}
      </div>

      {partialFailure !== null && (
        <div
          role="alert"
          data-testid="review-queue-partial-failure-banner"
          className="flex items-center gap-2 px-3 py-2 text-xs"
          style={{
            background: 'var(--warning-bg)',
            color: 'var(--warning-fg)',
            borderTop: '1px solid var(--warning-border)',
          }}
        >
          <span aria-hidden>⚠</span>
          <span className="flex-1">
            Envio parou no item {partialFailure.atItem} — {partialFailure.remaining}{' '}
            {partialFailure.remaining === 1 ? 'item não enviado' : 'itens não enviados'}
          </span>
          <button
            type="button"
            onClick={handleRetryClick}
            disabled={isSending}
            data-testid="review-queue-retry"
            className="btn btn--primary btn--sm"
          >
            {isSending ? 'Reenviando…' : 'Tentar novamente'}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            data-testid="review-queue-discard-remaining"
            className="btn btn--ghost btn--sm"
          >
            Descartar restantes
          </button>
        </div>
      )}

      {httpErrorMessage !== null && partialFailure === null && (
        <div
          role="alert"
          data-testid="review-queue-http-error"
          className="px-3 py-2 text-xs"
          style={{
            background: 'var(--warning-bg)',
            color: 'var(--warning-fg)',
            borderTop: '1px solid var(--warning-border)',
          }}
        >
          <span aria-hidden>⚠</span> {httpErrorMessage}
        </div>
      )}

      {partialFailure === null && (
        <div
          className="flex items-center justify-between gap-2 border-t px-3 py-2"
          style={{ borderColor: 'var(--border-subtle)' }}
        >
          {isBlocked ? (
            <>
              <span
                className="text-2xs"
                style={{ color: 'var(--warning-fg)' }}
                data-testid="review-queue-block-reason"
              >
                {aggregateCannedBlockReason(blockedItems, channel, mediaById)}
              </span>
              <button
                type="button"
                disabled
                data-testid="review-queue-send"
                className="btn btn--sm"
                style={{
                  background: 'var(--fg-4)',
                  color: 'var(--bg-surface)',
                  cursor: 'not-allowed',
                }}
                title="Resolva os itens em âmbar antes de enviar"
              >
                Enviar sequência ({pendingCount})
              </button>
            </>
          ) : (
            <>
              <span className="text-2xs" style={{ color: 'var(--fg-3)' }}>
                Itens serão enviados na ordem, um a um, como mensagens normais.
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onDismiss}
                  data-testid="review-queue-discard"
                  className="btn btn--ghost btn--sm"
                >
                  Descartar
                </button>
                <button
                  type="button"
                  onClick={handleSendClick}
                  disabled={isSending || pendingCount === 0}
                  data-testid="review-queue-send"
                  className="btn btn--primary btn--sm"
                >
                  {isSending ? 'Enviando…' : `Enviar sequência (${pendingCount})`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function QueueItemCard(props: {
  index: number;
  item: QueueItemState;
  channel: ChannelKind;
  variables: Record<string, string | null>;
  verified: boolean;
  mediaById: Map<string, MediaAssetWire>;
  rawTemplate: string | undefined;
  isEdited: boolean;
  isEditing: boolean;
  editValue: string;
  onEditValueChange: (v: string) => void;
  onStartEdit: () => void;
  onCommitEdit: () => void;
  onCancelEdit: () => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  readOnly: boolean;
}): JSX.Element {
  const { item, channel, mediaById, variables, verified } = props;
  const blocked =
    item.unresolved.length > 0 || evaluateCannedMediaBlock(item, channel, mediaById) !== null;
  const note = cannedItemNote(item, channel, mediaById, verified);
  const caption = item.kind === 'media' ? (item.caption ?? '') : '';
  const showHighlighted =
    !props.isEdited && props.rawTemplate !== undefined && props.rawTemplate !== '';

  return (
    <div
      data-testid={`queue-item-${item.itemId}`}
      className="flex items-start gap-2 border-b px-3 py-2"
      style={{
        borderColor: 'var(--border-subtle)',
        background: blocked ? 'var(--warning-bg)' : undefined,
      }}
    >
      <GripVertical size={14} aria-hidden style={{ color: 'var(--fg-4)', marginTop: 2 }} />
      <span className="text-xs font-medium" style={{ color: 'var(--fg-3)', minWidth: 16 }}>
        {props.index + 1}
      </span>
      <div className="min-w-0 flex-1">
        {item.kind === 'media' && (
          <MediaThumbnailRow mediaAssetId={item.mediaAssetId} mediaById={mediaById} />
        )}

        {props.isEditing ? (
          <textarea
            data-testid={`queue-item-textarea-${item.itemId}`}
            className="w-full rounded border px-2 py-1 text-sm"
            style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface-2)' }}
            rows={2}
            value={props.editValue}
            onChange={(e) => props.onEditValueChange(e.target.value)}
            onBlur={props.onCommitEdit}
            autoFocus
            aria-label={`Editar item ${(props.index + 1).toString()}`}
          />
        ) : (
          <div
            data-testid={`queue-item-text-${item.itemId}`}
            className="text-sm whitespace-pre-wrap"
            style={{ color: 'var(--fg-1)' }}
          >
            {item.kind === 'text' &&
              (showHighlighted && props.rawTemplate !== undefined
                ? renderTemplateText(props.rawTemplate, variables)
                : item.text)}
            {item.kind === 'media' &&
              caption !== '' &&
              (showHighlighted && props.rawTemplate !== undefined ? (
                <>Legenda: {renderTemplateText(props.rawTemplate, variables)}</>
              ) : (
                `Legenda: ${caption}`
              ))}
          </div>
        )}

        {note !== null && (
          <div
            data-testid={`queue-item-note-${item.itemId}`}
            className="mt-1 text-2xs"
            style={{ color: 'var(--warning-fg)' }}
          >
            {note}
          </div>
        )}

        {props.readOnly && (
          <div
            className="mt-1 text-2xs"
            style={{ color: item.status === 'failed' ? 'var(--danger-fg)' : 'var(--fg-3)' }}
          >
            {item.status === 'failed'
              ? `Falhou${item.failureReason !== undefined ? `: ${item.failureReason}` : ''}`
              : 'Não enviado'}
          </div>
        )}
      </div>

      {!props.readOnly && (
        <div className="flex items-center gap-1">
          {props.isEditing ? (
            <button
              type="button"
              aria-label="Salvar edição"
              data-testid={`queue-item-save-${item.itemId}`}
              onClick={props.onCommitEdit}
              className="composer-r7b__icon-btn"
            >
              OK
            </button>
          ) : (
            <button
              type="button"
              aria-label="Editar item"
              data-testid={`queue-item-edit-${item.itemId}`}
              onClick={props.onStartEdit}
              className="composer-r7b__icon-btn"
            >
              <Pencil size={13} aria-hidden />
            </button>
          )}
          <button
            type="button"
            aria-label="Mover item para cima"
            data-testid={`queue-item-up-${item.itemId}`}
            onClick={props.onMoveUp}
            disabled={!props.canMoveUp}
            className="composer-r7b__icon-btn"
          >
            <ArrowUp size={13} aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Mover item para baixo"
            data-testid={`queue-item-down-${item.itemId}`}
            onClick={props.onMoveDown}
            disabled={!props.canMoveDown}
            className="composer-r7b__icon-btn"
          >
            <ArrowDown size={13} aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Remover item"
            data-testid={`queue-item-remove-${item.itemId}`}
            onClick={props.onRemove}
            className="composer-r7b__icon-btn"
          >
            <X size={13} aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}

function MediaThumbnailRow({
  mediaAssetId,
  mediaById,
}: {
  mediaAssetId: string | undefined;
  mediaById: Map<string, MediaAssetWire>;
}): JSX.Element {
  const asset = mediaAssetId !== undefined ? mediaById.get(mediaAssetId) : undefined;
  const preview = useMediaAssetObjectUrl(mediaAssetId ?? null);
  return (
    <div className="mb-1 flex items-center gap-2">
      {preview.url !== null ? (
        <img
          src={preview.url}
          alt={asset?.filename ?? 'Mídia'}
          style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
        />
      ) : (
        <div
          aria-hidden
          style={{ width: 40, height: 40, borderRadius: 4, background: 'var(--bg-surface-2)' }}
        />
      )}
      <span className="text-xs" style={{ color: 'var(--fg-2)' }}>
        {asset?.filename ?? 'Arquivo'}
      </span>
    </div>
  );
}
